/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ThemeTypography } from "@insite/mobius/globals/baseTheme";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getFeaturedDocumentsData from "@stoneage/App/Handlers/Documents/getFeaturedDocumentsData";
import { FeaturedDocumentItemInfoType } from "@stoneage/App/Handlers/Documents/getFeaturedDocumentsData";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import { trackAsImpersonation, trackGaButtonClickEvent } from "@stoneage/App/Utilities/DataLayer";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    titleStyle = "titleStyle",
    numberOfDocuments = "numberOfDocuments",
    documentType = "documentType",
    linkText = "linkText",
    url = "url",
    openInNewTab = "openInNewTab",
    colorTheme = "colorTheme",
    gaClickEvent = "gaClickEvent",
    gaBtnClickEventName = "gaBtnClickEventName",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.titleStyle]: string;
        [fields.numberOfDocuments]: number;
        [fields.documentType]: string;
        [fields.linkText]: string;
        [fields.url]: LinkFieldValue;
        [fields.openInNewTab]: boolean;
        [fields.colorTheme]: "light" | "dark";
        [fields.gaClickEvent]: boolean;
        [fields.gaBtnClickEventName]: string;
    };
}

const mapStateToProps = (state: CustomAppState) => {
    return {
        session: state.context.session,
        impersonation: trackAsImpersonation(state),
    };
};

const mapDispatchToProps = {
    getFeaturedDocumentsData,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface DocumentsStyles {
    widgetContainer?: InjectableCss;
    infoWrapper?: InjectableCss;
    linkWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    link?: LinkPresentationProps;
}

export const documentsStyles: DocumentsStyles = {
    widgetContainer: {
        css: css`
            padding: 20px;
            border-radius: 2px;
            display: flex;
            flex-direction: column;
            box-shadow: inset 4px 0 0 0 ${getColor("common.ui6")};
            height: 100%;
        `,
    },
    infoWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-bottom: 20px;
        `,
    },
    link: {
        css: css`
            span {
                display: flex;
                align-items: center;
            }
            svg {
                width: 14px;
                height: 14px;
            }
        `,
        icon: {
            iconProps: { src: "ChevronRight" },
            position: "right",
        },
    },
    title: {
        css: css`
            margin-bottom: 20px;
        `,
    },
    linkWrapper: {
        css: css`
            margin-top: auto;
        `,
    },
};

const Documents = ({ fields, getFeaturedDocumentsData, session, impersonation }: Props) => {
    const styles = documentsStyles;

    const [featuredDocuments, setFeaturedDocuments] = React.useState([]);

    React.useEffect(() => {
        if (fields.documentType) {
            getFeaturedDocumentsData({
                numberOfDocuments: fields.numberOfDocuments || 2,
                documentType: fields.documentType,
                onSuccess: result => {
                    setFeaturedDocuments(result.featuredDocuments);
                },
                onError: () => {
                    setFeaturedDocuments([]);
                },
            });
        }
    }, [fields.documentType]);

    const { title, titleStyle, url, linkText, openInNewTab, colorTheme } = fields;

    const getTextColor = () => (colorTheme === "light" ? "common.black" : "common.white");
    const getBackgroundColor = () => (colorTheme === "light" ? "common.white" : "common.ui10");

    const link = useGetLink(url);

    if (!featuredDocuments?.length) {
        return null;
    }

    const { gaBtnClickEventName, gaClickEvent } = fields;

    return (
        <StyledWrapper
            {...styles.widgetContainer}
            css={css`
                ${styles.widgetContainer?.css};
                background-color: ${getColor(getBackgroundColor())};
            `}
        >
            {title && (
                <Typography {...styles.title} variant={titleStyle as keyof ThemeTypography} color={getTextColor()}>
                    {title}
                </Typography>
            )}
            <StyledWrapper {...styles.infoWrapper}>
                {featuredDocuments?.length > 0 &&
                    featuredDocuments.map((doc: FeaturedDocumentItemInfoType, index) => {
                        let dateDisplay = "";
                        if (doc.lastUpdateDate) {
                            dateDisplay = getLocalizedDateTime({
                                dateTime: new Date(doc.lastUpdateDate),
                                language: session.language,
                                options: {
                                    year: "numeric",
                                    month: "numeric",
                                    day: undefined,
                                },
                            });
                        }
                        return (
                            <StyledWrapper
                                // eslint-disable-next-line react/no-array-index-key
                                key={index}
                                css={css`
                                    display: flex;
                                    flex-direction: column;
                                    margin-bottom: 16px;
                                `}
                            >
                                <Typography
                                    color={colorTheme === "light" ? "text.main" : "common.ui5"}
                                    size={12}
                                    lineHeight="18px"
                                    translate="no"
                                >
                                    {translate("Updated")}&nbsp;
                                    {dateDisplay}
                                </Typography>
                                <Link
                                    color={
                                        colorTheme === "light"
                                            ? BLUEPRINT_NAME === "stoneage"
                                                ? "primary"
                                                : "common.ui13"
                                            : "common.white"
                                    }
                                    href={doc.filePath}
                                    target="_blank"
                                    {...(gaClickEvent &&
                                        gaBtnClickEventName && {
                                            onClick: () => {
                                                trackGaButtonClickEvent(
                                                    gaBtnClickEventName,
                                                    doc.description,
                                                    impersonation,
                                                    undefined,
                                                    doc.filePath,
                                                );
                                            },
                                        })}
                                >
                                    {doc.description}
                                </Link>
                            </StyledWrapper>
                        );
                    })}
            </StyledWrapper>
            <StyledWrapper {...styles.linkWrapper}>
                <Link
                    href={link.url}
                    target={openInNewTab ? "_blank" : ""}
                    {...styles.link}
                    color={colorTheme === "light" ? "primary" : "common.white"}
                    {...(gaClickEvent &&
                        gaBtnClickEventName && {
                            onClick: () => {
                                trackGaButtonClickEvent(
                                    gaBtnClickEventName,
                                    linkText || link.title || "",
                                    impersonation,
                                    undefined,
                                    link.url,
                                );
                            },
                        })}
                >
                    {linkText || link.title}
                </Link>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const documents: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(Documents),
    definition: {
        group: "XNGAGE" as any,
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.titleStyle,
                displayName: "Title Style",
                editorTemplate: "DropDownField",
                defaultValue: "h4",
                fieldType: "General",
                options: [
                    { displayName: "Heading 3", value: "h3" },
                    { displayName: "Heading 4", value: "h4" },
                    { displayName: "Heading 5", value: "h5" },
                    { displayName: "Heading 6", value: "h6" },
                ],
            },
            {
                name: fields.numberOfDocuments,
                displayName: "Number of Documents Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 2,
                fieldType: "General",
            },
            {
                name: fields.documentType,
                editorTemplate: "SystemListDropDownField",
                systemListName: "DocumentTypes",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.linkText,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                validate(value: string | null) {
                    if (!value) {
                        return null;
                    }

                    return value.length > 40 ? "Max character allowed is 40" : null;
                },
            },
            {
                name: fields.url,
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
            },
            {
                name: fields.openInNewTab,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
            },
            {
                name: fields.colorTheme,
                editorTemplate: "DropDownField",
                defaultValue: "light",
                fieldType: "General",
                options: [
                    { displayName: "Light", value: "light" },
                    { displayName: "Dark", value: "dark" },
                ],
            },
            {
                name: fields.gaClickEvent,
                displayName: "Track GA4 Buttons Click Event",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
            },
            {
                name: fields.gaBtnClickEventName,
                displayName: "Btn Click Event Name",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                isVisible: item => item.fields.gaClickEvent,
            },
        ],
    },
};

export default documents;
